/**
 * Wishes Management Page
 *
 * Admin interface for browsing and managing wish clusters.
 * Layout: filter tabs on top, cluster list on left, detail panel on right.
 */

import { useState } from 'react';
import { Sparkles, RefreshCcw } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { useClusterList } from '@/features/wishes/hooks';
import { ClusterCard } from '@/features/wishes/components/ClusterCard';
import { ClusterDetail } from '@/features/wishes/components/ClusterDetail';
import type { ClusterStatus } from '@/features/wishes/types';

type StatusFilter = '' | ClusterStatus;

const STATUS_TABS: { value: StatusFilter; label: string }[] = [
  { value: '', label: '全部' },
  { value: 'open', label: '开放' },
  { value: 'planned', label: '已规划' },
  { value: 'in_progress', label: '进行中' },
  { value: 'done', label: '已完成' },
  { value: 'rejected', label: '已拒绝' },
];

export function WishesPage() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const { data, isLoading, error, refetch, isFetching } = useClusterList({
    status: statusFilter || undefined,
    sort_by: 'wish_count',
    limit: 100,
  });

  const clusters = data?.items ?? [];

  const handleSelectCluster = (id: number) => {
    setSelectedId((prev) => (prev === id ? null : id));
  };

  const handleCloseDetail = () => setSelectedId(null);

  return (
    <div className="flex flex-col h-full max-h-screen overflow-hidden">
      {/* Page header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center gap-3">
          <Sparkles className="w-5 h-5 text-indigo-500" />
          <div>
            <h1 className="text-xl font-bold text-gray-900">愿望池管理</h1>
            <p className="text-xs text-gray-500 mt-0.5">管理用户愿望群组 · 状态更新 · 通知派发</p>
          </div>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          title="刷新"
        >
          <RefreshCcw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Status filter tabs */}
      <div className="px-6 pt-3 pb-2 border-b border-gray-100 flex-shrink-0">
        <Tabs value={statusFilter} onValueChange={(v) => { setStatusFilter(v as StatusFilter); setSelectedId(null); }}>
          <TabsList>
            {STATUS_TABS.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {/* Main content: list + detail */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: cluster list */}
        <div
          className={`flex flex-col overflow-y-auto border-r border-gray-200 flex-shrink-0 ${
            selectedId !== null ? 'w-72' : 'flex-1'
          }`}
        >
          {isLoading && (
            <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
              加载中...
            </div>
          )}

          {!isLoading && error && (
            <div className="flex-1 flex items-center justify-center text-red-500 text-sm px-4 text-center">
              加载失败: {(error as Error).message}
            </div>
          )}

          {!isLoading && !error && clusters.length === 0 && (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-400 py-12">
              <Sparkles className="w-10 h-10 mb-3 text-gray-300" />
              <p className="text-sm">暂无愿望群组</p>
            </div>
          )}

          {!isLoading && !error && clusters.length > 0 && (
            <div className="p-3 space-y-1.5">
              {clusters.map((cluster) => (
                <ClusterCard
                  key={cluster.id}
                  cluster={cluster}
                  selected={selectedId === cluster.id}
                  onClick={() => handleSelectCluster(cluster.id)}
                />
              ))}
              {data && (
                <p className="text-xs text-gray-400 text-right pt-1 pr-1">
                  共 {data.total} 个群组
                </p>
              )}
            </div>
          )}
        </div>

        {/* Right: cluster detail */}
        {selectedId !== null && (
          <div className="flex-1 overflow-hidden">
            <ClusterDetail
              clusterId={selectedId}
              allClusters={clusters}
              onClose={handleCloseDetail}
            />
          </div>
        )}

        {selectedId === null && !isLoading && clusters.length > 0 && (
          <div className="flex-1 flex items-center justify-center text-gray-300 text-sm">
            选择一个群组查看详情
          </div>
        )}
      </div>
    </div>
  );
}
